import {
  IOnboardingAddressRequest,
  IOnboardingBankRequest,
  IOnboardingProfileResponse,
} from '@/@types/Onboarding';
import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { api } from './api';

/** Limite alinhado à API: `max:4096` (KB) em ProfilePhotoController */
export const PROFILE_PHOTO_MAX_MB = 4;
export const PROFILE_PHOTO_MAX_BYTES = PROFILE_PHOTO_MAX_MB * 1024 * 1024;

export function getProfilePhotoUploadErrorMessage(
  error: unknown,
  t: TFunction,
): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 413) {
      return t('common:file_too_large', { size: PROFILE_PHOTO_MAX_MB });
    }

    const imageErrors = error.response?.data?.errors?.image;
    if (Array.isArray(imageErrors) && imageErrors[0]) {
      return String(imageErrors[0]);
    }
    if (typeof imageErrors === 'string' && imageErrors) {
      return imageErrors;
    }

    const message = error.response?.data?.message;
    if (
      typeof message === 'string' &&
      message.trim() &&
      !/unexpected\s*error/i.test(message)
    ) {
      return message;
    }
  }

  return t('common:unexpected_error');
}

async function getProfile() {
  const response = await api.get<IOnboardingProfileResponse>('/v1/profile');

  return response.data.data;
}

async function updateBankAccount(
  data: IOnboardingBankRequest,
  challengeToken?: string,
) {
  const payload: IOnboardingBankRequest & { challenge_token?: string } = {
    ...data,
    ...(challengeToken ? { challenge_token: challengeToken } : {}),
  };

  return await api.post<IOnboardingAddressRequest>(
    '/v1/profile/bank-accounts',
    payload,
  );
}

async function updateAddress(data: IOnboardingAddressRequest) {
  return await api.post<IOnboardingAddressRequest>('/v1/profile/address', data);
}

async function updateInternationalBankAccounts({ data, accountId, challengeToken }) {
  const payload = {
    ...data,
    ...(challengeToken ? { challenge_token: challengeToken } : {}),
  };

  return await api.put(`/v1/usd-bank-accounts/${accountId}`, payload);
}

async function uploadProfilePhoto(photo: File) {
  const formData = new FormData();
  formData.append('image', photo);

  const response = await api.post('/v1/profile/photo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

export const ProfileService = {
  getProfile,
  updateAddress,
  updateInternationalBankAccounts,
  updateBankAccount,
  uploadProfilePhoto,
  getProfilePhotoUploadErrorMessage,
  PROFILE_PHOTO_MAX_MB,
  PROFILE_PHOTO_MAX_BYTES,
};
